import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { ErrorStateMatcher } from '@angular/material/core';
import { formatDistance } from 'date-fns';
import { BehaviorSubject, Subject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ErrorDetailsComponent } from '../components/error-details/error-details.component';
import { AlertService } from './alert.service';

@Injectable({
  providedIn: 'root'
})
export class UtilsService {
  mainNavHeightSubject = new BehaviorSubject<number>(0);
  resizeObserver: ResizeObserver | null = null;

  _errorsSubject = new BehaviorSubject<string[]>(["Hello world errors"]);

  constructor(
    private _alertService: AlertService,
    private _bottomSheet: MatBottomSheet
  ) { }

  onMainNavHeightChanged() {
    return this.mainNavHeightSubject;
  }

  watchMainNavHeight(element: HTMLElement) {

    this.resizeObserver = new ResizeObserver(elements => {
      const element = elements[0].target as HTMLElement;
      this.mainNavHeightSubject.next(element.offsetHeight);
    });

    this.resizeObserver.observe(element);
  }

  formatTime(dateTime: string | null | undefined) {
    if (!dateTime) return '-';
    return formatDistance(new Date(dateTime), new Date(), { addSuffix: true });
  }

  toDateString(dateTime: string): string {
    if (!dateTime) return '-';
    return new Date(dateTime).toLocaleString();
  }

  bestBeforeTime(dateTime: string | null | undefined, delimiter: string = "/"): string {
    if (!dateTime) return "-";

    try {
      const date: Date = new Date(dateTime);
      switch (delimiter) {
        case '/':
          return `${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()}`;
        case '-':
          return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
        default:
          return "-";
      }
    } catch (error) {
      return "-";
    }
  }

  /**
   * Logs API failures to the browser console so you can match them with Network tab / backend.
   * Full response `body` is included when `environment.verboseApiErrorLogging` is true (dev default).
   */
  private logApiError(error: unknown): void {
    if (error instanceof HttpErrorResponse) {
      const payload: Record<string, unknown> = {
        url: error.url,
        status: error.status,
        statusText: error.statusText,
        message: error.message,
      };
      if (environment.verboseApiErrorLogging) {
        payload.body = error.error;
      }
      console.error('[UtilsService.showError] HttpErrorResponse', payload);
      return;
    }
    console.error('[UtilsService.showError] non-HTTP error', error);
  }

  showError(error: unknown) {
    this.logApiError(error);

    if (!(error instanceof HttpErrorResponse)) {
      this._alertService.errorAlert(
        'A system error has occurred, please contact support if this keeps happening.'
      );
      return;
    }

    const res = error;
    const body = res.error;

    // Falsy body (null/undefined) is common for network failures or empty error responses — use Angular's message + status.
    if (body == null || body === '') {
      const detail = [res.message, res.status ? `HTTP ${res.status}` : '']
        .filter(Boolean)
        .join(' — ');
      this._alertService.errorAlert(
        detail ||
          'A system error has occurred, please contact support if this keeps happening.'
      );
      return;
    }

    if (typeof body === 'object' && body !== null && 'errors' in body && body.errors) {
      const extractedErrors: string[] = [];
      for (const key of Object.keys(body.errors as Record<string, string>)) {
        extractedErrors.push((body.errors as Record<string, string>)[key]);
      }
      this._errorsSubject.next(extractedErrors);
      const showDetails = extractedErrors.length > 0;
      this._alertService.errorAlert(
        (body as { message?: string }).message
          ? (body as { message: string }).message
          : 'An error occurred, please try again.',
        showDetails
      );
      return;
    }

    if (typeof body === 'object' && body !== null && 'message' in body && (body as { message?: string }).message) {
      this._alertService.errorAlert((body as { message: string }).message);
      return;
    }

    if (typeof body === 'string' && body.trim()) {
      this._alertService.errorAlert(body);
      return;
    }

    this._alertService.errorAlert(
      res.message || 'An error occurred, please try again.'
    );
  }


  errors() {
    return this._errorsSubject;
  }

  openErrorSheet(): void {
    this._bottomSheet.open(ErrorDetailsComponent);
  }

  getRandomInt(min: number, max: number): number {
    min = Math.ceil(min);
    max = Math.floor(max);
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }
}